'use client';

import type { PaginationMeta } from '@eliadmin/core/src/api/api-types';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { usePathname } from 'next/navigation';
import { useEffect, useState, type MouseEvent } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { writeCategoryFilterState } from '@/lib/category-filter-state';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function CategoryPagination({
  links,
  onPageClicked,
  scrollTarget = 'category-products',
}: {
  links: PaginationMeta['links'];
  onPageClicked?: (page: number) => void;
  scrollTarget?: string;
}) {
  const { t } = useTranslations();
  const pathname = usePathname();
  const { filter, page, setPage } = useCategoryStore();
  const [currentSearch, setCurrentSearch] = useState('');
  useEffect(() => setCurrentSearch(window.location.search), [filter, page]);

  function navigate(event: MouseEvent<HTMLAnchorElement>, targetPage: number) {
    // Keep native new-tab/window and keyboard link behavior. Ordinary clicks are shallow.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    setPage(targetPage);
    onPageClicked?.(targetPage);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    if (scrollTarget === '__top__') window.scrollTo({ top: 0, behavior });
    else document.getElementById(scrollTarget)?.scrollIntoView({ behavior, block: 'start' });
  }

  return (
    <nav className="ea-pagination flex flex-row items-center gap-2">
      {links?.map((link, index) => {
        let targetPage: number | undefined;
        if (link.url) {
          try {
            const rawPage = new URL(link.url, 'https://pagination.invalid').searchParams.get(
              'page',
            );
            const parsed = Number(rawPage ?? (link.label === '1' ? '1' : undefined));
            if (Number.isSafeInteger(parsed) && parsed > 0) targetPage = parsed;
          } catch {
            // Invalid/disabled API links stay non-interactive; never navigate to the API origin.
          }
        }
        const params = writeCategoryFilterState(new URLSearchParams(currentSearch), {
          filter,
          page: targetPage ?? page,
        });
        const href = `${pathname}${params.toString() ? `?${params}` : ''}`;
        const content =
          index === 0 ? (
            <LuChevronLeft className="h-4 w-4" aria-hidden />
          ) : index === links.length - 1 ? (
            <LuChevronRight className="h-4 w-4" aria-hidden />
          ) : (
            link.label
          );
        const active = targetPage === page;
        // Preserve compact mobile pagination without waiting on a JS breakpoint hook.
        const nearActive =
          !targetPage ||
          Math.abs(targetPage - page) <= 1 ||
          index <= 1 ||
          index >= links.length - 2;
        return (
          <Button
            key={`${link.label}-${index}`}
            aria-label={
              index === 0 ? t('text3529') : index === links.length - 1 ? t('text3530') : link.label
            }
            asChild={!!targetPage}
            disabled={!targetPage}
            variant={active ? 'default' : targetPage ? 'secondary' : 'ghost'}
            className={`ea-pagination__item h-9 w-9 p-1 ${nearActive ? '' : 'hidden sm:inline-flex'}`}
          >
            {targetPage ? (
              <a
                href={href}
                aria-current={active ? 'page' : undefined}
                aria-label={
                  // The arrows were announced as a bare page number.
                  index === 0
                    ? t('text3529')
                    : index === links.length - 1
                      ? t('text3530')
                      : String(targetPage)
                }
                onClick={event => navigate(event, targetPage!)}
              >
                {content}
              </a>
            ) : (
              content
            )}
          </Button>
        );
      })}
    </nav>
  );
}
